import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import {Row} from '@/products/flexible-web-toolkit/layout/row'
import type {
	BgColor,
	MobileStackOrder,
	RowWidthType,
	ContainerClassName,
} from '@/products/flexible-web-toolkit/layout/layout-common-types'
import type {Block} from '@/services/kontent-ai/types'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {getBackgroundTheme} from '@/products/flexible-web-toolkit/colors'
import {getContainerClass} from '../helpers'
import {spacingPadding} from '../../styles'

export type RowContentItem = IContentItem<{
	items: Elements.LinkedItemsElement
	rowType: Elements.MultipleChoiceElement
	snippetContainerResize: Elements.MultipleChoiceElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
	snippetLayoutBackgroundColorColor: Elements.MultipleChoiceElement
	/** New color picker from snippet_layout_background_color snippet */
	snippetLayoutBackgroundColorBrandColor: Elements.CustomElement
	mobileStackOrder: Elements.MultipleChoiceElement
	fiftyFiftyRowGap: Elements.MultipleChoiceElement
}>

export const RowBlock: Block<RowContentItem> = ({
	block,
	BlockMapper,
	page,
	...context
}) => {
	const rowWidthType: RowWidthType = (() => {
		switch (block.elements.rowType[0]?.codename) {
			case 'full_width': {
				return 'full-width'
			}
			case 'n50_50_split': {
				return 'fifty-fifty'
			}
			case 'n70_30_split': {
				return 'seventy-thirty'
			}
			case 'n30_70_split': {
				return 'thirty-seventy'
			}
			default: {
				return 'full-width'
			}
		}
	})()

	const containerSize: ContainerClassName = getContainerClass(
		block.elements.snippetContainerResize[0]?.codename ?? ''
	)

	const componentSpacing = (() => {
		switch (block.elements.snippetSpacingSpacing[0]?.codename) {
			case 'default': {
				return spacingPadding.default
			}
			case 'top': {
				return spacingPadding.top
			}
			case 'bottom': {
				return spacingPadding.bottom
			}
			case 'none': {
				return spacingPadding.none
			}
			default: {
				return spacingPadding.none
			}
		}
	})()

	// Priority: new color picker > legacy dropdown > page config > fallback
	const {colorId: brand} = colorWithFallback({
		blockColor:
			block.elements.snippetLayoutBackgroundColorBrandColor || undefined,
		pageConfig: page.elements.config[0],
		fallbackColorId: 'primary',
	})
	const legacySnippetColor: BgColor = (() => {
		const codename =
			block.elements.snippetLayoutBackgroundColorColor[0]?.codename
		if (codename) {
			return codename as BgColor
		}
		return 'none'
	})()

	// When the new color picker has a value, it takes precedence over the legacy dropdown
	// and directly sets the background color via backgroundThemes.
	// Also applies when no block-level color is set but page config provides a color.
	const hasNewPickerColor = Boolean(
		block.elements.snippetLayoutBackgroundColorBrandColor
	)
	const newPickerBgTheme =
		hasNewPickerColor && brand ? getBackgroundTheme(brand) : ''
	// If new picker is active, suppress legacy bgColor so it doesn't conflict
	const snippetColor: BgColor = hasNewPickerColor
		? 'none'
		: legacySnippetColor

	const liteBackground =
		legacySnippetColor === 'brand' && !hasNewPickerColor
			? ` !bg-${brand}-light bg-default-light-background`
			: ''

	const mobileStackOrder: MobileStackOrder = (() => {
		const codename = block.elements.mobileStackOrder[0]?.codename
		if (codename) {
			return codename as MobileStackOrder
		}
		return 'default'
	})()

	const fiftyFiftyRowGap =
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fiftyFiftyRowGap can be null
		block.elements.fiftyFiftyRowGap?.[0]?.codename === 'yes'

	return (
		<Row
			bgColor={snippetColor}
			className={clsx(componentSpacing, liteBackground, newPickerBgTheme)}
			containerClassName={containerSize}
			mobileStackOrder={mobileStackOrder}
			rowWidthType={rowWidthType}
			fiftyFiftyRowGap={fiftyFiftyRowGap}
		>
			{block.elements.items.map((eachBlock, key) => {
				return (
					<div
						className={clsx(rowWidthType.split('-')[key], 'w-full')}
						key={eachBlock.system.id}
					>
						<BlockMapper
							blocks={[eachBlock]}
							className="not-italic"
							page={page}
							{...context}
						/>
					</div>
				)
			})}
		</Row>
	)
}
